import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createInvoice, getJobs } from "../../lib/api";
import { ChipGroup } from "../jobs/components/ChipGroup";
import { ClientContactField, type ClientContactFieldValue } from "../clients/components/ClientContactField";
import { ScopeEditor } from "../costing/ScopeEditor";
import { DEFAULT_TAX_RATE_BPS } from "../costing/costing";
import { hasPricedLineItem, validateScopeDraft, type ScopeDraft } from "../costing/types";
import { Modal } from "../../components/Modal";
import type { JobRecord } from "../jobs/types";
import styles from "./InvoiceCreatePage.module.css";

/**
 * Who the invoice is for. The three modes are the three things the system can
 * know about a payer, in descending order:
 *
 * - `job`    — an existing client's existing job. The invoice attaches to it and
 *              is numbered with the job's internal reference.
 * - `client` — a directory client with no job behind the work.
 * - `manual` — a typed name, for one-off work for someone not on file.
 */
type BillToMode = "manual" | "client" | "job";

const MODE_OPTIONS: ReadonlyArray<{ value: BillToMode; label: string }> = [
  { value: "manual", label: "Anyone (type a name)" },
  { value: "client", label: "Existing client" },
  { value: "job", label: "Client + job" },
];

function emptyScope(): ScopeDraft {
  return { description: "", taxRateBps: DEFAULT_TAX_RATE_BPS, lineItems: [] };
}

function jobLabel(job: JobRecord): string {
  const ref = job.refInternal || "No reference";
  const where = job.propertyAddress || job.title || "Untitled job";
  return `${ref} — ${where}`;
}

/**
 * The board's own rule is that a job is invoiced once it is Done (DEC-QI-010),
 * and the Generate-invoice step on the card refuses anything else. Authoring an
 * invoice here is a deliberate act, so the rule warns rather than blocks — the
 * same posture the board takes when a job moves to In-progress without an
 * accepted quote (DEC-QI-003).
 */
function needsDoneConfirmation(job: JobRecord | undefined): boolean {
  return Boolean(job) && job?.status !== "done";
}

/** Board wording for a job's stage, for the confirmation copy. */
function stageLabel(status: string | undefined): string {
  if (!status) return "not finished";
  return status === "in-progress" ? "In progress" : status.charAt(0).toUpperCase() + status.slice(1);
}

/**
 * /invoices/create — build an invoice by hand.
 *
 * The pricing half is {@link ScopeEditor}, the same editor the job form's Scope
 * page renders, so a line item behaves identically in both places. What is
 * specific to this page is the bill-to above it: an invoice here does not have
 * to come from a job, which is the whole point of the route.
 */
export function InvoiceCreatePage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<BillToMode>("manual");
  const [clientId, setClientId] = useState<string | undefined>(undefined);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [propertyAddress, setPropertyAddress] = useState("");
  const [jobId, setJobId] = useState("");
  const [notes, setNotes] = useState("");
  const [scope, setScope] = useState<ScopeDraft>(emptyScope);

  const [jobs, setJobs] = useState<JobRecord[] | null>(null);
  const [jobsError, setJobsError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Open while the vendor confirms invoicing a job that is not Done. */
  const [confirmingStage, setConfirmingStage] = useState(false);

  // Jobs are only needed by one of the three modes, so the list is fetched the
  // first time that mode is chosen rather than on page load.
  useEffect(() => {
    if (mode !== "job" || jobs !== null) return;
    let active = true;
    getJobs()
      .then((data) => {
        if (active) setJobs(data.jobs);
      })
      .catch((loadError: unknown) => {
        if (active) {
          setJobsError(loadError instanceof Error ? loadError.message : "Could not load jobs");
        }
      });
    return () => {
      active = false;
    };
  }, [mode, jobs]);

  // A job belongs to a client, so the list narrows to the chosen one; without a
  // client every job is fair game.
  const selectableJobs = useMemo(() => {
    if (!jobs) return [];
    const scoped = clientId ? jobs.filter((job) => job.clientId === clientId) : jobs;
    return [...scoped].sort((a, b) => (a.refInternal < b.refInternal ? 1 : -1));
  }, [jobs, clientId]);

  const selectedJob = selectableJobs.find((job) => job.id === jobId);

  const handleModeChange = (next: BillToMode): void => {
    setMode(next);
    setError(null);
    // Leaving a mode drops what only that mode could mean: a job is meaningless
    // without the job mode, and a directory id is meaningless once the name is
    // being typed by hand.
    if (next !== "job") setJobId("");
    if (next === "manual") setClientId(undefined);
  };

  const handleClientSelect = (client: ClientContactFieldValue | null): void => {
    setClientId(client?.id);
    setClientName(client?.name ?? "");
    // The chosen job belonged to the previous client.
    setJobId("");
  };

  const handleJobChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const nextId = event.target.value;
    setJobId(nextId);
    const job = selectableJobs.find((candidate) => candidate.id === nextId);
    // The job's own property is better than anything typed here, and the job is
    // where the invoice will read its client from anyway.
    if (job) {
      setPropertyAddress(job.propertyAddress ?? "");
      if (job.clientName) setClientName(job.clientName);
    }
  };

  /** The first thing standing between this form and a valid invoice, or "". */
  const blocker = ((): string => {
    if (mode === "manual" && !clientName.trim()) return "Enter who the invoice is for.";
    if (mode === "client" && !clientId) return "Pick a client from the directory.";
    if (mode === "job" && !jobId) return "Pick the job this invoice is for.";
    if (!hasPricedLineItem(scope)) return "Add at least one priced line item.";
    return validateScopeDraft(scope);
  })();

  /** Creates the invoice. Reached directly, or through the Done confirmation. */
  const create = async (): Promise<void> => {
    setConfirmingStage(false);
    setSubmitting(true);
    setError(null);
    try {
      const { invoice } = await createInvoice({
        jobId: mode === "job" ? jobId : undefined,
        clientId: mode === "manual" ? undefined : clientId,
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        propertyAddress: propertyAddress.trim(),
        notes: notes.trim(),
        scope,
      });
      // The register is where an invoice lives once it exists; the new draft is
      // named in the query so the page can say which one just landed.
      navigate(`/invoices?created=${encodeURIComponent(invoice.number)}`);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not create the invoice");
      setSubmitting(false);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (blocker) {
      setError(blocker);
      return;
    }
    // Invoicing a job the board does not consider finished is allowed, but not
    // silently: say which stage it is at and let the vendor decide.
    if (mode === "job" && needsDoneConfirmation(selectedJob)) {
      setConfirmingStage(true);
      return;
    }
    void create();
  };

  return (
    <section className={styles.root}>
      <div className={styles.head}>
        <div className={styles.headline}>
          <h1 className={styles.title}>New invoice</h1>
          <p className={styles.lede}>
            Bill anyone: a job, a client on file, or a name typed here. The invoice is created as a
            draft — nothing is sent until you send it.
          </p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/invoices" className={styles.secondaryButton}>
            Back to invoices
          </Link>
        </div>
      </div>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <fieldset className={styles.fieldset} disabled={submitting}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Bill to</h2>
            <ChipGroup
              legend="Who is this invoice for?"
              name="billToMode"
              options={MODE_OPTIONS}
              value={mode}
              onChange={handleModeChange}
              hint="A job-linked invoice takes that job's internal reference as its number."
            />

            {mode === "manual" ? (
              <div className={styles.row}>
                <label className={styles.field}>
                  <span className={styles.label}>Bill to</span>
                  <input
                    className={styles.input}
                    name="clientName"
                    value={clientName}
                    placeholder="Name on the invoice"
                    onChange={(event) => setClientName(event.target.value)}
                  />
                  <span className={styles.note}>Printed as the invoice's "Bill to".</span>
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Email</span>
                  <input
                    className={styles.input}
                    name="clientEmail"
                    type="email"
                    value={clientEmail}
                    placeholder="Where to send it"
                    onChange={(event) => setClientEmail(event.target.value)}
                  />
                  <span className={styles.note}>
                    Optional — but the invoice can only be emailed if it has an address.
                  </span>
                </label>
              </div>
            ) : (
              <ClientContactField
                clientId={clientId}
                clientName={clientName}
                onSelectClient={handleClientSelect}
                // The invoice bills the organisation; a contact would go nowhere.
                onSelectContact={() => undefined}
                showContact={false}
              />
            )}

            {mode === "job" && (
              <label className={styles.field}>
                <span className={styles.label}>Job</span>
                <select
                  className={styles.select}
                  name="jobId"
                  value={jobId}
                  onChange={handleJobChange}
                  disabled={jobs === null && !jobsError}
                >
                  <option value="">
                    {jobs === null ? "Loading jobs…" : "Select a job…"}
                  </option>
                  {selectableJobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {jobLabel(job)}
                    </option>
                  ))}
                </select>
                <span className={styles.note}>
                  {jobsError
                    ? jobsError
                    : clientId
                      ? `${selectableJobs.length} job${selectableJobs.length === 1 ? "" : "s"} for this client.`
                      : "Pick a client above to narrow the list."}
                </span>
              </label>
            )}

            {mode !== "job" && (
              <label className={styles.field}>
                <span className={styles.label}>Property or site</span>
                <input
                  className={styles.input}
                  name="propertyAddress"
                  value={propertyAddress}
                  placeholder="Where the work was done"
                  onChange={(event) => setPropertyAddress(event.target.value)}
                />
                <span className={styles.note}>Optional. Printed under the bill-to name.</span>
              </label>
            )}

            {selectedJob && (
              <p className={styles.linked}>
                Numbered <strong>{selectedJob.refInternal || "with a new reference"}</strong>, and
                billed to {selectedJob.clientName || "the job's client"}
                {selectedJob.propertyAddress ? ` at ${selectedJob.propertyAddress}` : ""}.
                {needsDoneConfirmation(selectedJob) && (
                  /* Said here as well as in the dialog, so the stage is visible
                     while the invoice is being written rather than only at the end. */
                  <>
                    {" "}
                    <span className={styles.stageWarning}>
                      This job is {stageLabel(selectedJob.status)}, not Done — you will be asked to
                      confirm.
                    </span>
                  </>
                )}
              </p>
            )}
          </section>

          <ScopeEditor
            value={scope}
            onChange={setScope}
            descriptionTitle="What is being billed"
            descriptionPlaceholder="Describe the work this invoice covers"
            descriptionNote="Optional. Printed above the line items as the scope of work."
            itemsEmptyMessage="No line items yet. An invoice needs at least one priced line."
          />

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Notes</h2>
            <label className={styles.field}>
              <span className={styles.label}>Notes on the invoice</span>
              <textarea
                className={styles.textarea}
                name="notes"
                rows={3}
                value={notes}
                placeholder="Anything the client should read with this invoice"
                onChange={(event) => setNotes(event.target.value)}
              />
              <span className={styles.note}>
                Optional. Payment terms and bank details come from Documents settings — they do not
                belong here.
              </span>
            </label>
          </section>
        </fieldset>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <div className={styles.actions}>
          {blocker && !error && <span className={styles.blocker}>{blocker}</span>}
          <button type="submit" className={styles.primaryButton} disabled={submitting || Boolean(blocker)}>
            {submitting ? "Creating…" : "Create draft invoice"}
          </button>
        </div>
      </form>

      {confirmingStage && selectedJob && (
        <Modal
          title="Invoice a job that isn't Done?"
          subtitle={jobLabel(selectedJob)}
          size="sm"
          onClose={() => setConfirmingStage(false)}
          footer={
            <>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setConfirmingStage(false)}
              >
                Cancel
              </button>
              <button type="button" className={styles.primaryButton} onClick={() => void create()}>
                Create anyway
              </button>
            </>
          }
        >
          <p className={styles.confirmationCopy}>
            This job is at <strong>{stageLabel(selectedJob.status)}</strong>. Invoicing from the job
            card requires it to be Done; building one here does not, so the invoice will be created
            as a draft against a job that is not finished.
          </p>
        </Modal>
      )}
    </section>
  );
}
